/**
 * Admin-portal keyboard (Tab) behaviour fixes. Design only, no change to what any control does.
 *
 * 1. The @jfs/ui-new text inputs wrap the real <input> in a pressable box (so clicking anywhere on the
 *    field focuses it). In the browser that box is its own Tab stop, so Tab had to be pressed twice to
 *    go from one field to the next. Such boxes are taken out of the Tab order; the <input> inside stays.
 * 2. While the user is navigating with the keyboard, the focused element is marked with `data-kb-focus`
 *    (field / control / solid) so assets/index.css can highlight it with a background colour instead of
 *    the browser's outline box.
 */

const FIELD_SELECTOR = 'input:not([type="hidden"]), textarea, select'
const MAX_WRAPPER_DEPTH = 3

const depthBetween = (ancestor: Element, node: Element) => {
  let depth = 0
  let current: Element | null = node
  while (current && current !== ancestor) {
    current = current.parentElement
    depth++
  }
  return current ? depth : Infinity
}

// A pressable box sitting just around a single input, whose only job is to focus that input.
const isInputWrapper = (el: HTMLElement) => {
  if (el.matches(FIELD_SELECTOR) || el.getAttribute('tabindex') !== '0') {
    return false
  }
  const fields = el.querySelectorAll(FIELD_SELECTOR)
  return fields.length === 1 && depthBetween(el, fields[0]) <= MAX_WRAPPER_DEPTH
}

const removeWrapperTabStops = (root: ParentNode) => {
  root.querySelectorAll<HTMLElement>('[tabindex="0"]').forEach((el) => {
    if (isInputWrapper(el)) {
      el.setAttribute('tabindex', '-1')
    }
  })
}

const isSolid = (el: Element | null) => {
  if (!el) {
    return false
  }
  const style = window.getComputedStyle(el)
  if (style.backgroundImage && style.backgroundImage !== 'none') {
    return true
  }
  const bg = style.backgroundColor
  return !!bg && bg !== 'transparent' && !/rgba\(.*,\s*0\)$/.test(bg) && bg !== 'rgb(255, 255, 255)'
}

// The bordered box drawn around a text field (a few levels above the <input>).
const findFieldBox = (input: HTMLElement) => {
  let current: HTMLElement | null = input.parentElement
  for (let i = 0; current && i < 5; i++, current = current.parentElement) {
    if (parseFloat(window.getComputedStyle(current).borderTopWidth) > 0) {
      return current
    }
  }
  return null
}

let usingKeyboard = false

const clearMarks = () => {
  document.querySelectorAll('[data-kb-focus]').forEach((el) => el.removeAttribute('data-kb-focus'))
}

const markFocus = (target: EventTarget | null) => {
  clearMarks()
  if (!usingKeyboard || !(target instanceof HTMLElement) || target === document.body) {
    return
  }
  if (target.matches(FIELD_SELECTOR)) {
    ;(findFieldBox(target) ?? target).setAttribute('data-kb-focus', 'field')
  } else if (isSolid(target) || isSolid(target.firstElementChild)) {
    target.setAttribute('data-kb-focus', 'solid')
  } else {
    target.setAttribute('data-kb-focus', 'control')
  }
}

export const enableKeyboardFocusFixes = () => {
  if (typeof document === 'undefined') {
    return
  }
  removeWrapperTabStops(document)
  new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.type === 'attributes') {
        if (m.target instanceof HTMLElement && isInputWrapper(m.target)) {
          m.target.setAttribute('tabindex', '-1')
        }
      } else {
        m.addedNodes.forEach((node) => node instanceof HTMLElement && removeWrapperTabStops(node.parentNode ?? node))
      }
    })
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['tabindex'] })

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {
      usingKeyboard = true
    }
  })
  document.addEventListener('mousedown', () => {
    usingKeyboard = false
    clearMarks()
  })
  document.addEventListener('focusin', (e) => markFocus(e.target))
  document.addEventListener('focusout', clearMarks)
}
